package org.multiplatform.kotfolio.pages.kotlin.new_feature

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("/kotlin/new_feature/2.4.0")
@Composable
fun NewFeature_2_4_0_Page() {
    BlogLayout(
        PageProperties(
            "Kotlin 2.4.0",
            ""
        ),
        BlogProperties("kotlin", "Kotlin 2.4.0")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "collection-literals",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        //-Xcollection-literals
                        
                        kotlin {
                            sourceSets.all {
                                languageSettings {
                                    optIn("kotlin.ExperimentalStdlibApi")
                                }
                            }
                            
                            compilerOptions {
                                freeCompilerArgs.addAll("-Xexplicit-backing-fields", "-Xexpect-actual-classes", "-Xcontext-parameters", "-Xcollection-literals")
                            }
                        }

                        val list = ["Kotlin", "Jetpack Compose", "Kotlin Multiplatform"]
                        
                        val users = [ User("Kamlesh", 21) ]
                        data class User(val name: String, val age: Int)
                         
                        val map = ["Kotlin": 1, "Jetpack Compose": 2, "Kotlin Multiplatform": 3]
                        
                        val map: MutableMap<String, Int> = ["one": 1, "two": 2, "three": 3] // creates a MutableMap
                    """.trimIndent()
            )

            SpanText(
                text = "context",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        class EmailSender
                        class SmsSender

                        context(emailSender: EmailSender)
                        fun sendNotification() {
                            println("Sent email notification")
                        }

                        context(smsSender: SmsSender)
                        fun sendNotification() {
                            println("Sent SMS notification")
                        }

                        context(defaultEmailSender: EmailSender, defaultSmsSender: SmsSender)
                        fun notifyUser() {

                            // Selects the overload with the EmailSender context parameter
                            sendNotification(emailSender = defaultEmailSender)

                            // Selects the overload with the SmsSender context parameter
                            sendNotification(smsSender = defaultSmsSender)
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                        //Kotlin/JS: js() with ES2015 destructuring This one is Kotlin/JS-specific
                        
                        fun main() {
                            val result = js(
                                ""$${'"'}
                                const user = { name: "Kamlesh", age: 25 };
                                const { name, age } = user;
                                `${name} is ${age}`;
                                ""$${'"'}
                            )
                            println(result)
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "companion object",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        class User(val name: String) {
                            companion object
                        }

                        fun User.Companion.createGuest(): User {
                            return User("Guest")
                        }

                        fun main() {
                            val guest = User.createGuest()
                            println(guest.name)
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "isSorted(), isSortedDescending(), isSortedWith(comparator), isSortedBy(selector), isSortedByDescending(selector)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                        data class User(val name: String, val age: Int)
                        
                        fun main() {

                            val numbers = listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9)
                            println("Numbers: isSorted")
                            println(numbers.isSorted()) // true
                            println("Is sorted descending")
                            println(numbers.isSortedDescending()) // false
                            
                            // Users list 
                            val users = listOf(
                                User("Alice", 21),
                                User("Balley", 34),
                                User("Charles", 29),
                            )

                            println("Users: isSorted by Age")
                            println(users.isSortedBy(User::age))  // false

                            val comparator = Comparator<User>{
                                user, user2 -> user.name.length.compareTo(user2.name.length)
                            }

                            println("Users: isSortedWith(comparator)")

                            val sortedList = users.sortedWith(comparator)

                            println("Users: Name by length ${sortedList.joinToString()}")
                            // Output: Users: Name by length User(name=Bob, age=31), User(name=Alice, age=24), User(name=Charlie, age=29)
                            println(sortedList.isSortedWith(comparator)) // true
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                        //Unnamed context parameter Use _ when the context value is only needed for resolution, not by name.
                        
                        interface Audit {
                            fun record(event: String)
                        }

                        context(_: Audit)
                        fun saveUser() {
                            println("User saved")
                        }

                        fun main() {
                            val audit = object : Audit {
                                override fun record(event: String) = println("AUDIT: $event")
                            }

                            context(audit) {
                                saveUser()
                            }
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "Explicit Backing Fields \"EBF\"",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    val uiState: StateFlow<UiState>
                        field = MutableStateFlow(UiState.Loading)
                    """.trimIndent()
            )

            SpanText(
                text = "Swift export",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        // Type String is preserved when exporting Flow
                        fun flowOfStrings(): Flow<String> = flowOf("hello", "any", "world")
                        
                        // Swift
                        var actual: [String] = []
                        // Type String is correctly inferred from Kotlin
                        for try await element in flowOfStrings().asAsyncSequence() {
                            actual.append(element)
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "@get",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                       @get:Size(max = 10)
                       var tags: List<String> = emptyList()
                """.trimIndent()
            )

            SpanText(
                text = "JS code",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                        fun spreadExample(): dynamic = js(""${'"'}
                            const add = (a, b, c) => a + b + c;

                            const nums = [1, 2, 3];
                            const sum = add(...nums);

                            const a = [1, 2, 3];
                            const b = [...a, 4, 5, 6];

                            return { sum, b: b };
                        ""${'"'})
                    """.trimIndent()
            )
        }
    }
}

//https://bloder.io/kotlin-context-parameters
//https://medium.com/@santimattius/kotlin-2-4-swift-export-flow-asyncsequence-and-swift-packages-6a97c3afe7b7
